import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { ibeTogglePinRead, ibeTogglePinSend } from 'actions/pin/beSalonPin.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import DELAY from 'constants/delay.const';
import IN from 'constants/in';
import LINK from 'constants/link.const';
import { useNavFor } from 'hooks/useNav.hook';
import { usePrepareReadWrite } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import delayed from 'utils/functions/delayed.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './PageHeader.module.scss';

const MIN_STY_WITH_EMAIL = 1;

const PageHeader = ({ onClick, onPinToggleSuccess, stylists }) => {
  const { t } = useTranslation();

  const {
    qr,
    mr,
    qj,
    mj,
    up: togglePin,
  } = usePrepareReadWrite({
    reader: ibeTogglePinRead,
    writer: ibeTogglePinSend,
    uOptions: { delay: DELAY.fast, refetch: true },
  });

  const pin = fromAnyToBool(mj?.enabledPinForAll ?? qj?.enabledPinForAll);
  const stylistsWithEmailCount = qj?.stylistsWithEmail;
  const stylistsWithoutEmailCount = qj?.stylistsWithoutEmail;
  const isLoading = qr?.isLoading || mr?.isLoading;

  const activeStylists = asArray(stylists).filter((s) => !s.serves_as_waitlist);
  const allStylistsHavePin = activeStylists.every((s) => s.has_pin_login);
  const isWaitlistWithoutEmail = asArray(stylists).some((s) => !s.email && !s.serves_as_waitlist);

  const totalStylists = activeStylists.length;
  const stylistsWithPin = activeStylists.filter((s) => s.has_pin_login).length;
  const stylistsWithoutPin = totalStylists - stylistsWithPin;

  const isPinEnabledForAll = allStylistsHavePin;
  const isDisabled = stylistsWithEmailCount < MIN_STY_WITH_EMAIL || (stylistsWithoutEmailCount > 0 && isWaitlistWithoutEmail);

  const nav = useNavFor('PageHeader()');
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();

  const { val: renderWarning, yay: showWarning, nay: hideWarning } = useTip(false);
  const { val: isEnabledModal, yay: showEnabledConfirmModal, nay: hideEnabledConfirmModal } = useTip(false);
  const { val: isDisabledModal, yay: showDisableModal, nay: hideDisableModal } = useTip(false);

  const handleTogglerClick = () => {
    if (isDisabled) {
      // if a stylist does not have an email -> warning message
      showWarning();
      delayed(DELAY.autohide, () => void hideWarning());
      return;
    }

    if (isPinEnabledForAll) {
      // all active -> we ask to disable all
      showDisableModal();
    } else {
      // there are inactive ones, but they all have email -> we enable
      handleToggleAll();
    }
  };

  const handleDisableAll = useCallback(async () => {
    const result = await togglePin({ data: { enabledPinForAll: !pin } });
    if (result.is.ok) {
      hideDisableModal();
      onPinToggleSuccess?.();
    }
  }, [togglePin, pin, hideDisableModal, onPinToggleSuccess]);

  const handleToggleAll = useCallback(async () => {
    if (pin) return void showDisableModal();
    const result = await togglePin({ data: { enabledPinForAll: !pin } });

    if (result.is.ok) {
      showEnabledConfirmModal();
      onPinToggleSuccess?.();
    }
  }, [pin, showDisableModal, togglePin, showEnabledConfirmModal, onPinToggleSuccess]);

  const handleWaitlist = useCallback(() => {
    nav.push({
      pathname: linkTo({ pattern: LINK.settingWaitlist, params: { salonId }, fault: '#' }),
    });
  }, [nav, salonId]);

  return (
    <div
      data-bem="PageHeader"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {isLoading && <OverlaySpinner />}

      <div className={CN.flex}>
        <div className={CN.pageTitle}>
          <div className={CN.pageIcon}>
            <Icon variant="userGroup" width="xl" />
          </div>
          {t('stylistScreen.title')}
        </div>

        <div className={CN.buttonContainer}>
          <Button variant="outline" xSpace onClick={handleWaitlist}>
            {t('stylistScreen.newWaitlistButton')}
          </Button>

          <Button variant="primaryForce" xSpace onClick={onClick}>
            {t('stylistScreen.newStaffButton')}
          </Button>
          <TooltipForReceptionist where={IN.btnAddStylist} />
        </div>

        <div className={CN.subTitle}>{t('stylistScreen.description')}</div>
        <div className={CN.alertOuterContainer}>
          {stylistsWithoutPin === totalStylists && (
            <AlertPanel tag="PageHeader__alert" variant="alert">
              <div>
                <div className={CN.alertTitle}>{t('stylistScreen.important')}</div>
                {t('stylistScreen.alertMessage1')}
              </div>
            </AlertPanel>
          )}
          {stylistsWithoutPin > 0 && stylistsWithPin > 0 && (
            <AlertPanel tag="PageHeader__alert" variant="error">
              <div>
                <div className={CN.alertTitle}>{t('stylistScreen.important')}</div>
                {stylistsWithoutPin > 1 ? t('stylistScreen.alertMessage3', { stylistsCount: stylistsWithoutPin }) : t('stylistScreen.alertMessage2', { stylistsCount: stylistsWithoutPin })}
              </div>
            </AlertPanel>
          )}
        </div>

        <div className={CN.alertOuterContainer}>
          <AlertPanel tag="PageHeader__alert-pin" variant="primary">
            <div className={CN.allPinContainer}>
              <div>{t('stylistScreen.pinCodeLoginNote')}</div>
              <div className={CN.togglerContainer}>
                <Toggler onClick={handleTogglerClick} selected={isPinEnabledForAll} onLabel={t('common.enabled').toUpperCase()} offLabel={t('common.disabled').toUpperCase()} />
                <TooltipForReceptionist where={CN.togglePinTooltip} />
              </div>
            </div>
          </AlertPanel>
        </div>
        {renderWarning && <div className={CN.renderWarning}>{t('stylistScreen.warningMsg')}</div>}
      </div>

      <SalonModal tag="PageHeader__modal_enable-all-pin" isOpen={isEnabledModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={hideEnabledConfirmModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus width="lg" variant="success">
            <Icon variant="padlock" width="l1" selected />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{t('stylistScreen.pinCodeModal.title1')}</div>
            <div className={CN.modalText}>{t('stylistScreen.pinCodeModal.description1')}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>

      <SalonModal tag="ModalsPanel__modal_9" isOpen={isDisabledModal} withFooter>
        <SalonModal.Body>
          <IconStatus variant="alert" width="lg">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{t('stylistScreen.pinCodeModal.title2')}</div>
            <div className={CN.modalText}>{t('stylistScreen.pinCodeModal.description2')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <Button variant="outlineForce" onClick={hideDisableModal} xSpace>
              {t('common.buttons.cancelUpp')}
            </Button>
            <Button variant="primaryForce" onClick={handleDisableAll} xSpace>
              {t('stylistScreen.pinCodeModal.disableButton')}
            </Button>
          </div>
        </SalonModal.Footer>
      </SalonModal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  stylists: state?.onboarding?.stylists?.stylists,
});

const PageHeaderHoc = connect(mapStateToProps)(PageHeader);

export default PageHeaderHoc;
